import { Group, Modal, ScrollArea, Stack, Text, TextInput, UnstyledButton } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import classes from './SecretMenuCommandPalette.module.css';
import {
  filterSecretMenuTools,
  SECRET_MENU_CATEGORIES,
  SecretMenuTool,
} from './secretMenuCatalogue';
import SecretMenuToolMarkers from './SecretMenuToolMarkers';

type SecretMenuCommandPaletteProps = {
  tools: readonly SecretMenuTool[];
  opened: boolean;
  onClose: () => void;
};

const categoryTitle = (tool: SecretMenuTool) =>
  SECRET_MENU_CATEGORIES.find(category => category.id === tool.category)?.title ?? '';

export default function SecretMenuCommandPalette({
  tools,
  opened,
  onClose,
}: SecretMenuCommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const activeRowRef = useRef<HTMLButtonElement>(null);

  const matches = filterSecretMenuTools(tools, { query, audiences: [], markers: [] });
  const clampedIndex = Math.min(activeIndex, Math.max(matches.length - 1, 0));

  useEffect(() => {
    activeRowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [clampedIndex]);

  const close = () => {
    setQuery('');
    setActiveIndex(0);
    onClose();
  };

  const select = (tool: SecretMenuTool) => {
    close();
    navigate(tool.href);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(Math.min(clampedIndex + 1, matches.length - 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(Math.max(clampedIndex - 1, 0));
      return;
    }
    if (event.key === 'Enter') {
      const tool = matches[clampedIndex];
      if (tool) {
        event.preventDefault();
        select(tool);
      }
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      withCloseButton={false}
      size="lg"
      styles={{ body: { padding: 0 } }}
    >
      <Stack gap={0}>
        <TextInput
          value={query}
          onChange={event => {
            setQuery(event.target.value);
            setActiveIndex(0);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Jump to a tool"
          leftSection={<IconSearch size={16} />}
          variant="unstyled"
          autoFocus
          px="sm"
          py="xxs"
        />
        {matches.length === 0 ? (
          <Text size="textSm" c="dimmed" px="sm" py="xs">
            Nothing matches that search.
          </Text>
        ) : (
          <ScrollArea.Autosize mah={360} px="xxs" pb="xxs">
            <Stack gap={0}>
              {matches.map((tool, index) => (
                <UnstyledButton
                  key={tool.slug}
                  ref={index === clampedIndex ? activeRowRef : undefined}
                  className={classes.row}
                  data-active={index === clampedIndex}
                  onClick={() => select(tool)}
                >
                  <Group gap="xxs" wrap="nowrap" justify="space-between">
                    <Group gap="xxs" wrap="wrap">
                      <Text size="textSm" fw={FONT_WEIGHTS.medium}>
                        {tool.label}
                      </Text>
                      <SecretMenuToolMarkers tool={tool} />
                    </Group>
                    <Text size="textXs" c="dimmed">
                      {categoryTitle(tool)}
                    </Text>
                  </Group>
                </UnstyledButton>
              ))}
            </Stack>
          </ScrollArea.Autosize>
        )}
      </Stack>
    </Modal>
  );
}
